import { CloseIcon } from '@calizahq/react-components-v2'
import { animated, config, useTransition } from '@react-spring/web'
import clsx from 'clsx'
import React, { useEffect, useRef } from 'react'

import { useMobile } from '@/hooks'

const AnimatedDiv = animated.div as React.ComponentType<{ [key: string]: any }>

// Track how many sheets are open so we only apply/remove scroll lock once
let openSheetCount = 0

type Props = {
  children: React.ReactNode
  header?: string
  isVisible: boolean
  mobileFullHeight?: boolean
  onClose: () => void
  renderFooter?: () => React.ReactNode
  renderHeader?: () => React.ReactNode
  showClose?: boolean
}
export const BottomSheet = ({
  children,
  header,
  isVisible,
  onClose,
  renderFooter,
  renderHeader,
  showClose = true,
  mobileFullHeight = false,
}: Props) => {
  const { isMobile } = useMobile()
  // Capture scrollbar width before any overflow change so measurement is accurate
  const scrollbarWidth = useRef(window.innerWidth - document.documentElement.clientWidth)

  useEffect(() => {
    // disable scroll of main body page behind sheet
    if (isVisible) {
      if (openSheetCount === 0) {
        document.body.style.paddingRight = `${scrollbarWidth.current}px`
        document.body.style.overflow = 'hidden'
      }
      openSheetCount++
    } else {
      openSheetCount = Math.max(0, openSheetCount - 1)
      if (openSheetCount === 0) {
        document.body.style.overflow = 'unset'
        document.body.style.paddingRight = ''
      }
    }
    return () => {
      // cleanup if component unmounts while visible
      if (isVisible) {
        openSheetCount = Math.max(0, openSheetCount - 1)
        if (openSheetCount === 0) {
          document.body.style.overflow = 'unset'
          document.body.style.paddingRight = ''
        }
      }
    }
  }, [isVisible])

  const transition = useTransition(isVisible, {
    from: { y: 800, opacity: 0 },
    enter: { y: 0, opacity: 1 },
    leave: { y: 800, opacity: 0 },
    config: { ...config.stiff, clamp: true },
  })

  const backdropTransition = useTransition(isVisible, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    immediate: true, // quickfix to prevent re-open on fast double click
  })

  const renderHeaderContent = () => {
    if (!!renderHeader) {
      return <div className="px-6">{renderHeader()}</div>
    } else if (!!header) {
      return (
        <div className="px-6 pt-6">
          <p className="font-medium text-header-md leading-none">{header}</p>
        </div>
      )
    } else {
      return null
    }
  }

  return (
    <>
      {backdropTransition(
        (styles, item) =>
          item && <AnimatedDiv className="fixed inset-0 z-40 bg-black-30" onClick={onClose} style={styles} />,
      )}

      {transition(
        (styles, item) =>
          item && (
            <AnimatedDiv
              aria-modal={true}
              className={clsx(
                isMobile
                  ? 'fixed left-0 right-0 bottom-0 z-50 flex w-full flex-col bg-white'
                  : 'fixed left-1/2 top-1/2 z-50 min-w-120 rounded-lg bg-white',
                !mobileFullHeight && isMobile && 'rounded-t-2xl',
                mobileFullHeight && isMobile && 'h-screen',
              )}
              role="dialog"
              style={{
                ...styles,
                maxHeight: isMobile && !mobileFullHeight ? '80%' : 'initial',
                transform: isMobile ? '' : 'translate(-50%, -50%)',
                height: isMobile && mobileFullHeight ? '' : 'initial',
              }}
            >
              <div className="flex items-center justify-end px-6 pt-6">
                {showClose && (
                  <CloseIcon className="cursor-pointer text-black" height={24} onClick={onClose} width={24} />
                )}
              </div>
              {renderHeaderContent()}
              <div className="overflow-auto pb-6 pt-4 md:max-h-[80vh] flex-1">{children}</div>
              {!!renderFooter && <div className="w-full shrink-0 border-t border-secondary">{renderFooter()}</div>}
            </AnimatedDiv>
          ),
      )}
    </>
  )
}
